import React, { useState, useCallback } from 'react';
import { updateContingencyStatus } from '../../agents/contingencyAgent';
import './ContingencyStateMachinePanel.css';

const CONTINGENCY_STATUS_TRANSITIONS = {
  pending:                    ['active'],
  active:                     ['report_uploaded', 'appraisal_entered', 'satisfied', 'expired', 'exercised'],
  report_uploaded:            ['analyzing', 'analysis_complete'],
  analyzing:                  ['analysis_complete'],
  analysis_complete:          ['repair_requested', 'satisfied'],
  repair_requested:           ['repair_responded', 'expired'],
  repair_responded:           ['repair_agreed', 'repair_requested', 'exercised'],
  repair_agreed:              ['satisfied', 'removed'],
  appraisal_entered:          ['gap_resolution_requested', 'satisfied'],
  gap_resolution_requested:   ['gap_resolution_responded'],
  gap_resolution_responded:   ['satisfied', 'gap_resolution_requested', 'exercised'],
  satisfied:                  ['removed'],
  removed:                    [],
  expired:                    [],
  exercised:                  [],
};

const STATUS_META = {
  pending:                    { label: 'Pending',                   color: '#9ca3af', icon: '⏳' },
  active:                     { label: 'Active',                    color: '#6366F1', icon: '🔵' },
  report_uploaded:            { label: 'Report Uploaded',           color: '#6366F1', icon: '📄' },
  analyzing:                  { label: 'AI Analyzing',              color: '#F59E0B', icon: '🔍' },
  analysis_complete:          { label: 'Analysis Complete',         color: '#10B981', icon: '✅' },
  repair_requested:           { label: 'Repair Requested',          color: '#F59E0B', icon: '🔧' },
  repair_responded:           { label: 'Seller Responded',          color: '#6366F1', icon: '💬' },
  repair_agreed:              { label: 'Repair Agreed',             color: '#10B981', icon: '🤝' },
  appraisal_entered:          { label: 'Appraisal Entered',         color: '#6366F1', icon: '🏠' },
  gap_resolution_requested:   { label: 'Gap Resolution Requested',  color: '#F59E0B', icon: '📋' },
  gap_resolution_responded:   { label: 'Gap Resolution Responded',  color: '#6366F1', icon: '💬' },
  satisfied:                  { label: 'Satisfied ✓',              color: '#10B981', icon: '✅' },
  removed:                    { label: 'Removed',                   color: '#9ca3af', icon: '🗑' },
  expired:                    { label: 'Expired',                   color: '#EF4444', icon: '⏰' },
  exercised:                  { label: 'Exercised (Exit)',          color: '#EF4444', icon: '🚪' },
};

const TERMINAL_STATUSES = ['removed', 'expired', 'exercised'];

function getActionVariant(status) {
  if (['satisfied', 'repair_agreed', 'removed'].includes(status)) return 'success';
  if (['expired', 'exercised'].includes(status)) return 'danger';
  return 'ghost';
}

export default function ContingencyStateMachinePanel({ contingency, onRefresh }) {
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackType, setFeedbackType] = useState(null);

  const status = contingency?.status;
  const meta = STATUS_META[status] || { label: status, color: '#9ca3af', icon: '❓' };
  const nextStates = CONTINGENCY_STATUS_TRANSITIONS[status] || [];
  const isTerminal = TERMINAL_STATUSES.includes(status);

  const handleConfirm = useCallback(async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await updateContingencyStatus(contingency.id, pending, note || null);
      setFeedback('Status updated!');
      setFeedbackType('success');
      setTimeout(() => {
        setPending(null);
        setNote('');
        setFeedback(null);
        setFeedbackType(null);
        onRefresh();
      }, 800);
    } catch (err) {
      setFeedback(err.message || 'Failed to update status');
      setFeedbackType('error');
    } finally {
      setSaving(false);
    }
  }, [contingency, pending, note, onRefresh]);

  if (!contingency) return null;

  return (
    <div className="csm-root">
      {/* Current status */}
      <div className="csm-current">
        <span className="csm-current-label">Current Status</span>
        <span className="csm-status-pill" style={{ background: meta.color }}>
          {meta.icon} {meta.label}
        </span>
        {contingency.resolution_summary && (
          <p className="csm-resolution-note">"{contingency.resolution_summary}"</p>
        )}
      </div>

      {/* Terminal state */}
      {isTerminal && (
        <div className="csm-terminal-banner">
          {status === 'expired' && '⏰ This contingency has expired.'}
          {status === 'exercised' && '🚪 This contingency was exercised — deal exited.'}
          {status === 'removed' && '✓ This contingency has been removed.'}
        </div>
      )}

      {/* Next steps */}
      {!isTerminal && nextStates.length > 0 && (
        <div className="csm-next">
          <div className="csm-next-label">Available Transitions</div>
          <div className="csm-next-actions">
            {nextStates.map(s => (
              <button
                key={s}
                className={`csm-action-btn ${getActionVariant(s)}`}
                onClick={() => setPending(s)}
                disabled={saving}
              >
                {STATUS_META[s]?.icon} {STATUS_META[s]?.label || s}
              </button>
            ))}
          </div>

          {/* Inline confirmation */}
          {pending && (
            <div className="csm-confirm">
              <p className="csm-confirm-msg">
                Transition to <strong>{STATUS_META[pending]?.label}</strong>?
              </p>
              <textarea
                className="csm-confirm-note"
                placeholder="Optional note for this transition…"
                rows={2}
                value={note}
                onChange={e => setNote(e.target.value)}
              />
              {feedback && <div className={`csm-feedback ${feedbackType}`}>{feedback}</div>}
              <div className="csm-confirm-actions">
                <button className="csm-btn-confirm" onClick={handleConfirm} disabled={saving}>
                  {saving ? 'Saving…' : 'Confirm'}
                </button>
                <button
                  className="csm-btn-cancel"
                  onClick={() => { setPending(null); setNote(''); setFeedback(null); }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isTerminal && nextStates.length === 0 && (
        <div className="csm-terminal-banner">No further actions available.</div>
      )}
    </div>
  );
}
